import { Injectable } from '@angular/core';
import * as ExcelJS from 'exceljs';
import { ImportConfig } from '../interfaces/import-config.interface';

export interface ExcelSheet {
  name: string;
  data: any[][];
  headers: string[];
  processedData: any[];
}

export interface ProcessedExcelFile {
  fileName: string;
  sheets: ExcelSheet[];
  defaultSheet: ExcelSheet;
}

@Injectable({
  providedIn: 'root'
})
export class ExcelProcessorService {

  async validateExcelHeaders(file: File, config: ImportConfig): Promise<{
    isValid: boolean;
    missingColumns: string[];
    warnings: string[]
  }> {
    try {
      const arrayBuffer = await this.fileToArrayBuffer(file);
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(arrayBuffer);

      // Get the first sheet (or configured sheet)
      const sheetIndex = config.sheetIndex || 0;
      const worksheet = workbook.worksheets[sheetIndex];

      if (!worksheet || worksheet.rowCount === 0) {
        return {
          isValid: false,
          missingColumns: ['El archivo no contiene datos'],
          warnings: []
        };
      }

      // Extract headers from first row
      const headers: string[] = [];
      const firstRow = worksheet.getRow(1);
      firstRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        let value = cell.value;

        if (value && typeof value === 'object') {
          if ('text' in value) {
            value = value.text;
          } else if ('result' in value) {
            value = value.result;
          }
        }

        headers[colNumber - 1] = String(value || '').trim();
      });

      // Validate each configured column
      const missingColumns: string[] = [];
      const warnings: string[] = [];

      config.columns.forEach(columnConfig => {
        const headerIndex = this.findHeaderIndex(headers, columnConfig);

        if (headerIndex === -1) {
          const columnName = columnConfig.excelColumnName || columnConfig.displayName;

          if (columnConfig.required) {
            missingColumns.push(columnName);
          } else {
            warnings.push(`Columna opcional no encontrada: ${columnName}`);
          }
        }
      });

      return {
        isValid: missingColumns.length === 0,
        missingColumns,
        warnings
      };

    } catch (error) {
      return {
        isValid: false,
        missingColumns: [`Error validando headers: ${error instanceof Error ? error.message : 'Error desconocido'}`],
        warnings: []
      };
    }
  }

  async processFile(file: File, config: ImportConfig): Promise<ProcessedExcelFile> {
    try {
      const arrayBuffer = await this.fileToArrayBuffer(file);
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(arrayBuffer);
      
      const sheets = await this.extractAllSheets(workbook, config);
      const defaultSheet = sheets[config.sheetIndex || 0] || sheets[0];
      
      return {
        fileName: file.name,
        sheets,
        defaultSheet
      };
    } catch (error) {
      throw new Error(`Error procesando archivo Excel: ${error instanceof Error ? error.message : 'Error desconocido'}`);
    }
  }

  private async extractAllSheets(workbook: ExcelJS.Workbook, config: ImportConfig): Promise<ExcelSheet[]> {
    const sheets: ExcelSheet[] = [];
    
    workbook.eachSheet((worksheet) => {
      if (worksheet.rowCount > 0) {
        const sheetData = this.extractSheetData(worksheet);
        const processedData = this.processSheetData(sheetData, config);
        
        sheets.push({
          name: worksheet.name,
          data: sheetData.rawData,
          headers: sheetData.headers,
          processedData
        });
      }
    });
    
    return sheets;
  }

  private extractSheetData(worksheet: ExcelJS.Worksheet): { rawData: any[][], headers: string[] } {
    const rawData: any[][] = [];
    const headers: string[] = [];
    
    worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
      const rowData: any[] = [];
      
      row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        let value = cell.value;
        
        if (value && typeof value === 'object') {
          if ('text' in value) {
            value = value.text;
          } else if ('result' in value) {
            value = value.result;
          }
        }
        
        rowData[colNumber - 1] = this.cleanCellValue(value);
      });
      
      if (rowNumber === 1) {
        headers.push(...rowData.map(h => String(h || '').trim()));
      } else {
        rawData.push(rowData);
      }
    });
    
    return { rawData, headers };
  }

  private processSheetData(sheetData: { rawData: any[][], headers: string[] }, config: ImportConfig): any[] {
    const { headers, rawData } = sheetData;
    const processedData: any[] = [];
    
    // Filter out completely empty rows first
    const filteredRawData = rawData.filter(row => {
      // Check if at least one cell has a non-empty value
      return row.some(cell => {
        return cell !== null && 
               cell !== undefined && 
               cell !== '' && 
               String(cell).trim() !== '';
      });
    });
    
    filteredRawData.forEach((row, rowIndex) => {
      const processedRow: any = {};
      
      config.columns.forEach(columnConfig => {
        const headerIndex = this.findHeaderIndex(headers, columnConfig);
        const cellValue = row[headerIndex];
        
        processedRow[columnConfig.key] = this.processeCellValue(
          cellValue,
          columnConfig,
          rowIndex
        );
      });
      
      processedData.push(processedRow);
    });
    
    return processedData;
  }

  private findHeaderIndex(headers: string[], columnConfig: any): number {
    const searchTerms = [
      columnConfig.excelColumnName,
      columnConfig.displayName,
      columnConfig.key
    ].filter(Boolean);
    
    for (const term of searchTerms) {
      const index = headers.findIndex(header => 
        header?.toLowerCase().trim() === term?.toLowerCase().trim()
      );
      if (index !== -1) return index;
    }
    
    return -1;
  }

  private processeCellValue(value: any, columnConfig: any, rowIndex: number): any {
    if (value === null || value === undefined || value === '') {
      // Support defaultValue as function or static value
      if (columnConfig.defaultValue !== undefined) {
        // If defaultValue is a function, execute it
        if (typeof columnConfig.defaultValue === 'function') {
          return columnConfig.defaultValue();
        }
        // Otherwise use the static value directly
        return columnConfig.defaultValue;
      }
      return null;
    }
    
    let processedValue = value;
    
    switch (columnConfig.dataType) {
      case 'number':
        processedValue = this.parseNumber(value);
        break;
      case 'date':
        processedValue = this.parseDate(value);
        break;
      case 'boolean':
        processedValue = this.parseBoolean(value);
        break;
      case 'string':
        processedValue = String(value).trim();
        break;
    }
    
    if (columnConfig.transform && typeof columnConfig.transform === 'function') {
      processedValue = columnConfig.transform(processedValue);
    }
    
    return processedValue;
  }

  private parseNumber(value: any): number | null {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const cleaned = value.replace(/[,\s]/g, '');
      const parsed = parseFloat(cleaned);
      return isNaN(parsed) ? null : parsed;
    }
    return null;
  }

  private parseDate(value: any): Date | null {
    if (value instanceof Date) return value;
    if (typeof value === 'string' || typeof value === 'number') {
      const parsed = new Date(value);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  }

  private parseBoolean(value: any): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
      const lower = value.toLowerCase().trim();
      return ['true', 'yes', 'sí', 'si', '1', 'verdadero'].includes(lower);
    }
    if (typeof value === 'number') {
      return value !== 0;
    }
    return false;
  }

  private cleanCellValue(value: any): any {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string') {
      return value.trim();
    }
    return value;
  }

  private fileToArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as ArrayBuffer);
      reader.onerror = () => reject(new Error('Error reading file'));
      reader.readAsArrayBuffer(file);
    });
  }

  validateFileType(file: File): boolean {
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ];
    return validTypes.includes(file.type) || 
           file.name.toLowerCase().endsWith('.xlsx') || 
           file.name.toLowerCase().endsWith('.xls');
  }

  validateFileSize(file: File, maxSize: number = 10 * 1024 * 1024): boolean {
    return file.size <= maxSize;
  }

  async validateRowCount(file: File, maxRows: number = 1000): Promise<{ isValid: boolean, actualRows: number }> {
    try {
      const arrayBuffer = await this.fileToArrayBuffer(file);
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(arrayBuffer);
      
      let maxRowCount = 0;
      
      workbook.eachSheet((worksheet) => {
        if (worksheet.rowCount > 0) {
          let nonEmptyRowCount = 0;
          
          worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
            // Skip header row
            if (rowNumber === 1) return;
            
            // Check if row has at least one non-empty cell
            let hasValue = false;
            row.eachCell({ includeEmpty: true }, (cell) => {
              let value = cell.value;
              
              // Handle formula cells
              if (value && typeof value === 'object') {
                if ('text' in value) {
                  value = value.text;
                } else if ('result' in value) {
                  value = value.result;
                }
              }
              
              // Check if cell has a meaningful value
              if (value !== null && 
                  value !== undefined && 
                  value !== '' && 
                  String(value).trim() !== '') {
                hasValue = true;
              }
            });
            
            if (hasValue) {
              nonEmptyRowCount++;
            }
          });
          
          maxRowCount = Math.max(maxRowCount, nonEmptyRowCount);
        }
      });
      
      return {
        isValid: maxRowCount <= maxRows,
        actualRows: maxRowCount
      };
    } catch (error) {
      throw new Error(`Error validating row count: ${error instanceof Error ? error.message : 'Error desconocido'}`);
    }
  }

  async exportToExcel(data: any[], fileName: string): Promise<void> {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Registros Inválidos');
      
      if (data.length === 0) return;
      
      // Get headers from first row
      const headers = Object.keys(data[0]);
      
      // Add header row
      worksheet.addRow(headers);
      
      // Style the header row
      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE6E6FA' }
      };
      
      // Add data rows
      data.forEach(row => {
        const values = headers.map(header => row[header] || '');
        worksheet.addRow(values);
      });
      
      // Auto-fit columns
      worksheet.columns.forEach((column, index) => {
        let maxLength = headers[index]?.length || 10;
        data.forEach(row => {
          const cellValue = String(row[headers[index]] || '');
          maxLength = Math.max(maxLength, cellValue.length);
        });
        column.width = Math.min(maxLength + 2, 50);
      });
      
      // Generate buffer and download
      const buffer = await workbook.xlsx.writeBuffer();
      this.downloadFile(buffer, fileName);
      
    } catch (error) {
      throw new Error(`Error exportando a Excel: ${error instanceof Error ? error.message : 'Error desconocido'}`);
    }
  }

  private downloadFile(buffer: ArrayBuffer, fileName: string): void {
    const blob = new Blob([buffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}